import { createSlice, PayloadAction } from 'ngrx-slice';

import {
  IncomingPurchaseListResponse,
  OutgoingPurchaseListResponse,
  PageableObject,
  PurchaseDto,
  PurchaseStatusUpdateRequest,
  SortObject,
} from '@app/api/marketplace/models';
import { PurchaseDtoRequest } from '@app/purchase-lists/interfaces/purchase-dto-request';
import { PurchaseListDtoRequest } from '@app/purchase-lists/interfaces/purchase-list-dto-request';
import { paginatorConfiguration } from '@shared/constants/paginator-configuration';
import { ApiErrorResponseWithId } from '@shared/interfaces/api-error-response';
import { PageResponse } from '@shared/interfaces/page-response.interface';

import { ProgressStatusType } from '../interfaces/purchase-progress-status-type';

export interface State {
  purchaseListsIncomingFilter: PurchaseListDtoRequest;
  purchaseListsOutgoingFilter: PurchaseListDtoRequest;
  purchaseListsIncoming: PageResponse<IncomingPurchaseListResponse>;
  purchaseListsOutgoing: PageResponse<OutgoingPurchaseListResponse>;
  activePurchaseList: PurchaseDto | null;
  isLoading: boolean;
  loadError: boolean;
  purchaseStatusSaving: boolean;
  purchaseStatusSaveError?: PayloadAction<ApiErrorResponseWithId>;
}

export const initialListState = {
  totalPages: 0,
  totalElements: 0,
  size: 0,
  content: [],
  number: 0,
  sort: {} as SortObject,
  pageable: {} as PageableObject,
  first: true,
  last: true,
  numberOfElements: 0,
  empty: true,
};

export const initialState: State = {
  purchaseListsIncomingFilter: {
    page: paginatorConfiguration.defaultPageIndex,
    size: paginatorConfiguration.pageSizeOptions[0],
    sort: [],
    status: ProgressStatusType.InProgress,
  },
  purchaseListsOutgoingFilter: {
    page: paginatorConfiguration.defaultPageIndex,
    size: paginatorConfiguration.pageSizeOptions[0],
    sort: [],
    status: ProgressStatusType.InProgress,
  },
  purchaseListsIncoming: initialListState,
  purchaseListsOutgoing: initialListState,
  activePurchaseList: null,
  isLoading: false,
  loadError: false,
  purchaseStatusSaving: false,
  purchaseStatusSaveError: undefined,
};

export const {
  actions: PurchaseListsActions,
  selectors,
  ...PurchaseListsFeature
} = createSlice({
  name: 'purchase-lists',
  initialState,
  reducers: {
    loadPurchaseListsIncoming: {
      trigger: (
        state,
        action: PayloadAction<{ filter: PurchaseListDtoRequest }>
      ) => {
        state.purchaseListsIncoming = initialListState;
        state.purchaseListsIncomingFilter = action.filter;
        state.isLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{
          response: PageResponse<IncomingPurchaseListResponse>;
        }>
      ) => {
        state.purchaseListsIncoming = action.response;
        state.isLoading = false;
        state.loadError = false;
      },
      failure: (state) => ({
        ...state,
        purchaseListLoadError: true,
        isLoading: false,
      }),
    },
    loadPurchaseListsOutgoing: {
      trigger: (
        state,
        action: PayloadAction<{ filter: PurchaseListDtoRequest }>
      ) => {
        state.purchaseListsOutgoing = initialListState;
        state.purchaseListsOutgoingFilter = action.filter;
        state.isLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{
          response: PageResponse<OutgoingPurchaseListResponse>;
        }>
      ) => {
        state.purchaseListsOutgoing = action.response;
        state.isLoading = false;
        state.loadError = false;
      },
      failure: (state) => ({
        ...state,
        purchaseListLoadError: true,
        isLoading: false,
      }),
    },
    loadActivePurchaseList: {
      trigger: (
        state,
        action: PayloadAction<{ request: PurchaseDtoRequest }>
      ) => {
        state.activePurchaseList = null;
        state.isLoading = true;
      },
      success: (
        state,
        action: PayloadAction<{
          response: PurchaseDto;
        }>
      ) => {
        state.activePurchaseList = action.response;
        state.isLoading = false;
        state.loadError = false;
      },
      failure: (state) => ({
        ...state,
        purchaseListLoadError: true,
        isLoading: false,
      }),
    },
    updatePurchaseStatus: {
      trigger: (
        state,
        _action: PayloadAction<{
          request: PurchaseStatusUpdateRequest;
        }>
      ) => {
        state.purchaseStatusSaving = true;
      },
      success: (state) => {
        state.purchaseStatusSaving = false;
        state.purchaseStatusSaveError = undefined;
      },
      failure: (state, action: PayloadAction<ApiErrorResponseWithId>) => {
        state.purchaseStatusSaving = false;
        state.purchaseStatusSaveError = action;
      },
    },
    reset: (_) => initialState,
  },
});

export const PurchaseListsSelectors = {
  ...selectors,
};
